import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { Icon, IconArrowDown, IconArrowRight } from '@flixbus/honeycomb-icons-react';

import Pagination from 'common/Pagination';
import TablePreloader from 'common/TablePreloader';
import { crewMemberDetailsStore } from 'pages/CrewMemberDetails/crewMemberDetailsStore';

import { ACTION_TYPE_LABELS } from './const';
import { generateCrewLogEntry } from './CrewLogEntryGenerator/crewLogEntryGenerator';
import CrewLogDateRangeFilter from './CrewLogTimeFilter';
import CrewLogTypeFilter from './CrewLogTypeFilter';
import { renderCrewLogTypeTag } from './CrewLogTypeTag';
import { crewMemberActionLogStore } from './CrewMemberActionLogStore';
import { CREW_MEMBER_ACTION_LOG_TEXT } from './text';
import { getRouteFromArray, isWaLogType, renderParamRow, renderWaSummary } from './WaLogDetails';

import styles from './styles.module.css';

const timezoneOffset = format(new Date(), 'xxx');

const WaActionLogRow = ({ parameters, createdAt, createdBy, type }) => {
	const [expanded, setExpanded] = useState(false);
	const route = getRouteFromArray(parameters);
	const firstParam = parameters[0];
	const typename = firstParam?.__typename;
	const driverName = crewMemberDetailsStore.employeeInfo?.fullName;

	return (
		<div className={styles.rowCard}>
			<div className={styles.rowGrid}>
				<div>
					<strong>{ACTION_TYPE_LABELS[type] ?? type}</strong>
				</div>
				<div>
					<div className={styles.detailsLine}>
						{driverName && <span className={styles.driverName}>{driverName}</span>}
						{route && <span> - {route}</span>}
						{parameters.length > 1 && (
							<span className={styles.versionBadge}>{parameters.length}</span>
						)}
						<button
							type="button"
							className={styles.expandToggle}
							onClick={() => setExpanded((prev) => !prev)}
							aria-expanded={expanded}
						>
							<Icon InlineIcon={expanded ? IconArrowDown : IconArrowRight} size={3} />
						</button>
					</div>
					{firstParam && (
						<div className={styles.summaryLine}>{renderWaSummary(type, firstParam)}</div>
					)}
				</div>
				<div>{renderCrewLogTypeTag(typename)}</div>
				<div>{format(parseISO(createdAt), 'd MMM, HH:mm')}</div>
				<div>{createdBy}</div>
				{expanded && (
					<div className={styles.expandedDetails}>
						{parameters.map((param) => (
							<div key={`${param.stationFrom}-${param.stationTo}`} className={styles.paramRow}>
								{renderParamRow(type, param)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

const resolveHoldDownName = (originalCrewMemberId) => {
	const holdDowns = crewMemberDetailsStore.editData?.holdDowns;
	if (!holdDowns || !originalCrewMemberId) return null;
	const match = holdDowns.find((hd) => hd.originalCrewMember.id === originalCrewMemberId);
	return match?.originalCrewMember.fullName ?? null;
};

const ActionLogRow = ({ parameters, createdAt, createdBy, type }) => {
	const displayParams = parameters.originalCrewMemberId
		? {
				...parameters,
				originalCrewMemberName:
					parameters?.originalCrewMemberFullName ||
					resolveHoldDownName(parameters.originalCrewMemberId),
			}
		: parameters;

	return (
		<div className={styles.rowCard}>
			<div className={styles.rowGrid}>
				<div>
					<strong>{ACTION_TYPE_LABELS[type] ?? type}</strong>
				</div>
				<div>{generateCrewLogEntry(displayParams, type)}</div>
				<div>{renderCrewLogTypeTag(parameters.__typename)}</div>
				<div>{format(parseISO(createdAt), 'd MMM, HH:mm')}</div>
				<div>{createdBy}</div>
			</div>
		</div>
	);
};

const CrewMemberActionLog = observer(() => {
	const { employeeId } = crewMemberDetailsStore;

	const { isLoading, logs, isEmptyLog, totalPages, currentPage, setCurrentPage, totalCount, size } =
		crewMemberActionLogStore;

	useEffect(() => {
		if (employeeId === null) return;
		crewMemberActionLogStore.initialize(employeeId);
	}, [employeeId]);
	return (
		<>
			<div className={styles.filters}>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>{CREW_MEMBER_ACTION_LOG_TEXT.periodLabel}</span>
					<CrewLogDateRangeFilter />
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>{CREW_MEMBER_ACTION_LOG_TEXT.categoryLabel}</span>
					<CrewLogTypeFilter />
				</div>
			</div>
			{isLoading && (
				<TablePreloader
					rowsCount={10}
					headers={[
						CREW_MEMBER_ACTION_LOG_TEXT.actionHeader,
						CREW_MEMBER_ACTION_LOG_TEXT.detailsHeader,
						CREW_MEMBER_ACTION_LOG_TEXT.categoryHeader,
						CREW_MEMBER_ACTION_LOG_TEXT.dateTimeHeader,
						CREW_MEMBER_ACTION_LOG_TEXT.userHeader,
					]}
				/>
			)}

			{!isLoading && (
				<div className={styles.actionLog}>
					<div className={styles.headerRow}>
						<div>{CREW_MEMBER_ACTION_LOG_TEXT.actionHeader}</div>
						<div>{CREW_MEMBER_ACTION_LOG_TEXT.detailsHeader}</div>
						<div>{CREW_MEMBER_ACTION_LOG_TEXT.categoryHeader}</div>
						<div>
							{CREW_MEMBER_ACTION_LOG_TEXT.dateTimeHeader}
							<br />
							(UTC {timezoneOffset})
						</div>
						<div>{CREW_MEMBER_ACTION_LOG_TEXT.userHeader}</div>
					</div>
					{isEmptyLog ? (
						<div className={styles.noActions}>{CREW_MEMBER_ACTION_LOG_TEXT.emptyRow}</div>
					) : (
						logs.map((log) =>
							isWaLogType(log.type) ? (
								<WaActionLogRow
									key={log.id}
									parameters={log.parameters}
									createdAt={log.createdAt}
									createdBy={log.createdBy}
									type={log.type}
								/>
							) : (
								<ActionLogRow
									key={log.id}
									parameters={log.parameters}
									createdAt={log.createdAt}
									createdBy={log.createdBy}
									type={log.type}
								/>
							)
						)
					)}
				</div>
			)}
			{!isLoading && (
				<div className={styles.pagination}>
					<Pagination
						activePage={currentPage}
						setActivePage={setCurrentPage}
						pageCount={{ size: size, totalCount: totalCount, totalPages: totalPages }}
					/>
				</div>
			)}
		</>
	);
});

export default CrewMemberActionLog;
